import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Activity, Eye, MoreHorizontal } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import type { ViagemBilling, ViagemControle } from '../../types';
import { DataTableHelper, Formatter } from 'utils';
import { formatViagemCurrency, parseViagemCurrencyValue } from '../../utils';

const billingDt = new DataTableHelper<ViagemBilling>();
const viagemDt = new DataTableHelper<ViagemControle>();

const formatDate = (value?: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm' }) : '---';
};

const formatTime = (value?: string | null) => {
  return value || '---';
};

const parseDurationToMinutes = (value?: string | null) => {
  if (!value) return 0;

  const [hours = 0, minutes = 0] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const formatDuration = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const getBillingTotalValue = (billing: ViagemBilling) => {
  if (billing.totalValue !== null && billing.totalValue !== undefined) {
    return billing.totalValue;
  }

  const totalFromItems = billing.billingItems?.reduce(
    (total, item) =>
      total + parseViagemCurrencyValue(item.viagem?.valorEstadia),
    0
  );

  return totalFromItems || null;
};

const getBillingTotalHours = (billing: ViagemBilling) => {
  if (!billing.billingItems) return '---';

  return formatDuration(
    billing.billingItems.reduce(
      (total, item) =>
        total + parseDurationToMinutes(item.viagem?.estadiaPagar),
      0
    )
  );
};

const getApprovalTooltip = (row: ViagemControle) => {
  if (row.billingItem) {
    return {
      action: 'Fatura',
      user: null,
      date: null,
      billingId: row.billingItem.viagemBillingId,
    };
  }

  if (row.approvedAt) {
    return {
      action: 'Aprovado por',
      user: row.approvedByUser,
      date: row.approvedAt,
    };
  }

  if (row.rejectedAt) {
    return {
      action: 'Reprovado por',
      user: row.rejectedByUser,
      date: row.rejectedAt,
    };
  }

  return null;
};

export const useViagensBillingColumns = ({
  onShowDetails,
  onShowActivities,
}: {
  onShowDetails: (billing: ViagemBilling) => void;
  onShowActivities: (billing: ViagemBilling) => void;
}): ColumnDef<ViagemBilling>[] => {
  return React.useMemo(
    () => [
      billingDt.buildColumn({
        id: 'id',
        title: 'ID',
        accessorKey: 'id',
        content: (row) => row.id,
      }),
      billingDt.buildColumn({
        id: 'carrier',
        title: 'Transportadora',
        enableSorting: false,
        content: (row) => row.carrier?.tradeName || '---',
      }),
      billingDt.buildColumn({
        id: 'totalItems',
        title: 'Qtd. viagens',
        accessorKey: 'totalItems',
        content: (row) => row.totalItems ?? row.billingItems?.length ?? 0,
      }),
      billingDt.buildColumn({
        id: 'totalHours',
        title: 'Horas',
        enableSorting: false,
        content: (row) => getBillingTotalHours(row),
      }),
      billingDt.buildColumn({
        id: 'totalValue',
        title: 'Valor total',
        accessorKey: 'totalValue',
        content: (row) => formatViagemCurrency(getBillingTotalValue(row)),
      }),
      billingDt.buildColumn({
        id: 'createdAt',
        title: 'Criada em',
        accessorKey: 'createdAt',
        content: (row) => formatDate(row.createdAt),
      }),
      billingDt.buildColumn({
        id: 'user',
        title: 'Criada por',
        enableSorting: false,
        content: (row) => row.user?.name || '---',
      }),
      billingDt.buildColumn({
        id: 'approvalStatus',
        title: 'Status',
        enableSorting: false,
        content: (row) => {
          const isReversed = Boolean(row.reversedAt) || Boolean(row.activities?.some((activity) => activity.operation === 'REVERSE'));
          const label = isReversed ? 'Estornada' : row.approvedAt ? 'Aprovada' : row.rejectedAt ? 'Reprovada' : 'Pendente';
          const className = isReversed
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : row.approvedAt
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : row.rejectedAt
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-slate-50 text-slate-600 border-slate-200';
          return <span className={`inline-flex rounded-sm border px-2 py-1 text-xs font-GilroySemibold ${className}`}>{label}</span>;
        },
      }),
      billingDt.buildColumn({
        id: 'acoes',
        title: 'Ações',
        enableSorting: false,
        enableHiding: false,
        content: (row) => (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              asChild
              onClick={(event) => event.stopPropagation()}
            >
              <Button variant="ghost" className="ml-auto h-8 w-8 p-0">
                <MoreHorizontal className="h-5 w-5 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem
                className="font-sans cursor-pointer"
                onClick={() => onShowDetails(row)}
              >
                <Eye className="mr-2 h-4 w-4" />
                Detalhes
              </DropdownMenuItem>
              <DropdownMenuItem
                className="font-sans cursor-pointer"
                onClick={() => onShowActivities(row)}
              >
                <Activity className="mr-2 h-4 w-4" />
                Atividades
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      }),
    ],
    [onShowActivities, onShowDetails]
  );
};

export const useViagensToBillingColumns = ({
  onShowActivities,
}: {
  onShowActivities?: (viagem: ViagemControle) => void;
} = {}): ColumnDef<ViagemControle>[] => {
  return React.useMemo(
    () => [
      viagemDt.buildSelectionByPageColumn(),
      viagemDt.buildColumn({
        id: 'idViagem',
        title: 'ID viagem',
        accessorKey: 'idViagem',
        content: (row) => row.idViagem || '---',
      }),
      viagemDt.buildColumn({
        id: 'shipment',
        title: 'Shipment',
        accessorKey: 'shipment',
        content: (row) => row.shipment || '---',
      }),
      viagemDt.buildColumn({
        id: 'notas',
        title: 'Nota',
        accessorKey: 'notas',
        content: (row) => row.notas || '---',
      }),
      viagemDt.buildColumn({
        id: 'placa',
        title: 'Placa',
        accessorKey: 'placa',
        content: (row) => row.placa || '---',
      }),
      viagemDt.buildColumn({
        id: 'agendamento',
        title: 'Agendamento',
        accessorKey: 'agendamento',
        content: (row) => formatDate(row.agendamento),
      }),
      viagemDt.buildColumn({
        id: 'chegada',
        title: 'Chegada',
        accessorKey: 'chegada',
        content: (row) => formatDate(row.chegada),
      }),
      viagemDt.buildColumn({
        id: 'fimDescarregamento',
        title: 'Fim descarregamento',
        accessorKey: 'fimDescarregamento',
        content: (row) => formatDate(row.fimDescarregamento),
      }),
      viagemDt.buildColumn({
        id: 'permanenciaRec',
        title: 'Permanência REC',
        accessorKey: 'permanenciaRec',
        content: (row) => formatTime(row.permanenciaRec),
      }),
      viagemDt.buildColumn({
        id: 'fimCarregamento',
        title: 'Fim carregamento',
        accessorKey: 'fimCarregamento',
        content: (row) => formatDate(row.fimCarregamento),
      }),
      viagemDt.buildColumn({
        id: 'saida',
        title: 'Saída',
        accessorKey: 'saida',
        content: (row) => formatDate(row.saida),
      }),
      viagemDt.buildColumn({
        id: 'permanenciaExp',
        title: 'Permanência EXP',
        accessorKey: 'permanenciaExp',
        content: (row) => formatTime(row.permanenciaExp),
      }),
      viagemDt.buildColumn({
        id: 'totalPermanencia',
        title: 'Total permanência',
        accessorKey: 'totalPermanencia',
        content: (row) => formatTime(row.totalPermanencia),
      }),
      viagemDt.buildColumn({
        id: 'limiteRestante',
        title: 'Limite restante',
        accessorKey: 'limiteRestante',
        content: (row) => formatTime(row.limiteRestante),
      }),
      viagemDt.buildColumn({
        id: 'estadiaPagar',
        title: 'Estadia pagar',
        accessorKey: 'estadiaPagar',
        content: (row) => formatTime(row.estadiaPagar),
      }),
      viagemDt.buildColumn({
        id: 'valorEstadia',
        title: 'Valor estadia',
        accessorKey: 'valorEstadia',
        content: (row) => formatViagemCurrency(row.valorEstadia),
      }),
      viagemDt.buildColumn({
        id: 'approvalProtocol',
        title: 'Protocolo',
        accessorKey: 'approvalProtocol',
        content: (row) => row.approvalProtocol || '---',
      }),
      viagemDt.buildColumn({
        id: 'status',
        title: 'Status',
        accessorKey: 'status',
        content: (row) => row.status || '---',
      }),
      viagemDt.buildColumn({
        id: 'approvalStatus',
        title: 'Aprovação',
        enableSorting: false,
        content: (row) => {
          const billing = row.billingItem?.billing;
          const label = billing?.reversedAt
            ? 'Revalidar'
            : billing?.rejectedAt
            ? 'Rejeitada'
            : billing?.approvedAt
            ? 'Faturada'
            : row.approvedAt
            ? 'Aprovado'
            : row.rejectedAt
            ? 'Rejeitado'
            : 'Pendente';
          const className = billing?.reversedAt
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : billing?.rejectedAt
            ? 'bg-red-50 text-red-700 border-red-200'
            : billing?.approvedAt
            ? 'bg-blue-50 text-blue-700 border-blue-200'
            : row.approvedAt
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : row.rejectedAt
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-slate-50 text-slate-600 border-slate-200';
          const tooltip = getApprovalTooltip(row);
          const badge = (
            <span
              className={`inline-flex rounded-sm border px-2 py-1 text-xs font-GilroySemibold ${className}`}
            >
              {label}
            </span>
          );

          if (!tooltip) return badge;

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>{badge}</TooltipTrigger>
                <TooltipContent side="top" className="bg-slate-700 text-white">
                  <div className="flex flex-col gap-1">
                    {'billingId' in tooltip ? (
                      <span className="font-GilroySemibold">
                        Vinculada à fatura {tooltip.billingId}
                      </span>
                    ) : (
                      <span className="font-GilroySemibold">
                        {tooltip.action}{' '}
                        {tooltip.user?.name || 'Usuário não identificado'}
                      </span>
                    )}
                    {!('billingId' in tooltip) && tooltip.user?.email && (
                      <span className="text-xs text-slate-100">
                        {tooltip.user.email}
                      </span>
                    )}
                    {!('billingId' in tooltip) && tooltip.date && (
                      <span className="text-xs text-slate-100">
                        {formatDate(tooltip.date)}
                      </span>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      }),
      viagemDt.buildColumn({
        id: 'acoes',
        title: 'Ações',
        enableSorting: false,
        enableHiding: false,
        content: (row) => {
          if (!onShowActivities) return <div className="text-right">---</div>;

          return (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                asChild
                onClick={(event) => event.stopPropagation()}
              >
                <Button variant="ghost" className="ml-auto h-8 w-8 p-0">
                  <MoreHorizontal className="h-5 w-5 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white"
                onClick={(event) => event.stopPropagation()}
              >
                <DropdownMenuItem
                  className="font-sans cursor-pointer"
                  onClick={() => onShowActivities(row)}
                >
                  <Activity className="mr-2 h-4 w-4" />
                  Atividades
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      }),
    ],
    [onShowActivities]
  );
};
